/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { SEPARATOR_HEIGHT } from "abreact/input/AbList";

/**
 * Data on every tree list item.
 */

/**
 * Data on a normal tree list item (not a special case of loading, not found, or separator)
 */

/**
 * Represents a tree node that is pending an async return of children
 */
export const LOADING = Symbol("Tree Loading Children");

/**
 * Represents an error. An index is trying to be shown that the
 * AbTreeRootNode doesn't know how to find. Should never appear.
 */
export const NOT_FOUND = Symbol("Tree Not Found");

/**
 * Represents a tree separator.
 */
export const SEPARATOR = Symbol("Tree Separator");

/**
 * These are values that should be rendered by the tree,
 * not passed back to the user's itemRenderer.
 */

/**
 * Data for a special-case tree list item.
 * 
 * The {@link AbTreeListItemInternal} interface should, like {@link AbTreeListItemStandard}, extend
 * {@link AbTreeListItemBase}.
 *
 * Next, we want to be able to specify a specific {@typeParam Value} if needed, but by default,
 * {@link AbTreeListItemInternal#value} should assume the full union type of {@link InternalValues}.
 *
 * Finally, since {@link AbTreeListItemInternal} is part of a discriminated union with
 * {@link AbTreeListItemStandard}, the rest of its keys should be all those found in {@link AbTreeListItemStandard}
 * not already accounted for in the previous two types ({@link AbTreeListItemBase} and
 * {@link AbTreeListItemInternal#value}), mapped to optional never.
 */

/**
 * Utility function for making an AbTreeListItem representing a separator.
 */
export function createSeparator(item) {
  return AbEnriched.create({
    value: SEPARATOR,
    ...item
  }, {
    displayOnly: true,
    rowHeight: SEPARATOR_HEIGHT,
    className: "ab-tree-list-separator-item"
  });
}

/**
 * Maps internal tree list items to unique key strings.
 */
export function toKey(item) {
  const {
    value,
    index,
    depth
  } = item;
  switch (value) {
    case LOADING:
      return `l_${index}_${depth}`;
    case NOT_FOUND:
      return `n_${index}_${depth}`;
    case SEPARATOR:
      return `s_${index}_${depth}`;
  }
}

/**
 * Union of the normal and special case tree list items,
 * making a type encompassing all that the tree can render.
 */
//# sourceMappingURL=AbTreeListItem.js.map
